import { useMemo, useRef } from "react";

/**
 * A safer, read-only alternative to `useRef` that always exposes the latest value.
 *
 * This hook provides a stable reference object whose `.current` value automatically
 * stays in sync with the latest `value` passed in — without triggering re-renders.
 *
 * **Why this exists:**
 * React’s `useRef` is mutable — meaning downstream code can accidentally overwrite
 * `ref.current`. It also doesn’t prevent stale closures when used inside callbacks,
 * effects, or event listeners that don’t re-run on every value change.
 * `useSyncedRef` solves this by returning a frozen, read-only ref object that:
 * - Always reads the latest value (no stale closures)
 * - Never allows `.current` to be reassigned
 * - Keeps the same identity across renders
 *
 * **When to use:**
 * - When you need access to the latest value inside callbacks, effects, or event
 *   listeners without re-running them or adding dependencies.
 * - When you want a ref-like object that’s *read-only* and cannot be mutated.
 *
 * **When *not* to use:**
 * - If you need a writable ref that you control manually (e.g. DOM refs or
 *   imperative handles) — use `useRef` instead.
 *
 * **Example:**
 * ```tsx
 * const valueRef = useSyncedRef(value);
 *
 * useEffect(() => {
 *   const id = setInterval(() => {
 *     console.log(valueRef.current); // always the latest value
 *   }, 1000);
 *   return () => clearInterval(id);
 * }, []);
 * ```
 *
 * @param value The value you want the ref to stay synced with.
 * @returns A frozen ref-like object whose `.current` always reflects the latest value.
 */
export function useSyncedRef<T>(value: T): { readonly current: T } {
  const ref = useRef(value);

  ref.current = value;

  return useMemo(
    () =>
      Object.freeze({
        get current() {
          return ref.current;
        },
      }),
    [],
  );
}
